import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Package, RefreshCw, Trash2, X, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmtNum = (n) => new Intl.NumberFormat('en-IN').format(n || 0);

export default function StockDebts() {
  const [rows, setRows] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending');

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/stock-debts?status=${status}`, { withCredentials: true }),
        axios.get(`${API}/stock-debts/dashboard`, { withCredentials: true }),
      ]);
      setRows(a.data || []);
      setDashboard(b.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const cancel = async (id) => {
    if (!window.confirm('Cancel this stock debt? (e.g. the client cancelled the order)')) return;
    try { await axios.post(`${API}/stock-debts/${id}/cancel`, {}, { withCredentials: true }); toast.success('Cancelled'); load(); }
    catch { toast.error('Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this stock debt record permanently?')) return;
    try { await axios.delete(`${API}/stock-debts/${id}`, { withCredentials: true }); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="stock-debts-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Stock Debts</h1>
          <p className="text-zinc-500 text-xs mt-0.5">FG debts clear when you next produce the product; RM debts clear automatically from the next inward lot of that material.</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg border border-zinc-800 text-zinc-400 hover:bg-zinc-800"><RefreshCw size={13} /></button>
      </div>

      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-4" data-testid="kpi-total-debts">
            <div className="text-[10px] text-red-300 uppercase tracking-wider">Pending debts</div>
            <div className="font-mono text-2xl font-bold text-zinc-50">{fmtNum(dashboard.total_debts)}</div>
          </div>
          <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-4" data-testid="kpi-products-owed">
            <div className="text-[10px] text-amber-300 uppercase tracking-wider">Products owed</div>
            <div className="font-mono text-2xl font-bold text-zinc-50">{fmtNum(dashboard.total_products_owed)}</div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">By Product</div>
            <div className="space-y-1 text-[11px] max-h-24 overflow-y-auto">
              {dashboard.by_product.map((p, i) => (
                <div key={i} className="flex justify-between border-b border-zinc-800/50 py-0.5">
                  <span className="text-zinc-300 truncate">{p.product_name}</span>
                  <span className="text-red-300 whitespace-nowrap">-{p.qty_outstanding} {p.unit}</span>
                </div>
              ))}
              {dashboard.by_product.length === 0 && <div className="text-zinc-500">No products owed.</div>}
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        {['pending', 'fulfilled', 'all'].map(s => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`stock-debts-status-${s}`}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${status === s ? 'bg-amber-500/15 border-amber-500/40 text-amber-300' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200'}`}>
            {s.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-zinc-800 text-zinc-500 text-[10px] uppercase tracking-wider">
              <th className="text-left px-3 py-2">Product / Material</th>
              <th className="text-left px-3 py-2">Client / Batch</th>
              <th className="text-left px-3 py-2">Bill · Settled from</th>
              <th className="text-right px-3 py-2">Qty Owed</th>
              <th className="text-right px-3 py-2">Fulfilled</th>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-zinc-500 animate-pulse">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-zinc-500"><CheckCircle size={24} className="text-green-400 mx-auto mb-2" />No {status} stock debts.</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-b border-zinc-800/50" data-testid={`stock-debt-row-${r.id}`}>
                <td className="px-3 py-2 text-zinc-100">
                  {r.category === 'rm' ? <><span className="text-[9px] uppercase tracking-wider text-amber-400 mr-1.5">RM</span>{r.material_name}</> : r.product_name}
                  {r.category === 'rm' && r.product_name && <div className="text-[10px] text-zinc-500">for {r.product_name}</div>}
                </td>
                <td className="px-3 py-2 text-zinc-300">{r.client || (r.batch_id ? <span className="font-mono text-sky-300">{r.batch_id}</span> : '—')}</td>
                <td className="px-3 py-2 text-zinc-400 font-mono">
                  {r.bill_no || r.bill_id || ''}
                  {(r.settled_from || []).length > 0 && (
                    <div className="space-y-0.5" data-testid={`debt-settled-from-${r.id}`}>
                      {r.settled_from.map((sf, i) => (
                        <div key={i} className="text-[10px] text-emerald-300">{sf.lot_no} · {sf.qty} {r.unit} · {(sf.at || '').slice(0, 10)}</div>
                      ))}
                    </div>
                  )}
                  {r.category === 'rm' && !(r.settled_from || []).length && r.status === 'pending' && <span className="text-[10px] text-zinc-600">awaiting inward</span>}
                </td>
                <td className="px-3 py-2 text-right text-zinc-100 font-mono">{r.qty} {r.unit}</td>
                <td className="px-3 py-2 text-right text-green-300 font-mono">{r.qty_fulfilled || 0} {r.unit}</td>
                <td className="px-3 py-2 text-zinc-500">{r.created_at?.slice(0, 10)}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                    r.status === 'fulfilled' ? 'bg-green-900/40 text-green-300' :
                    r.status === 'cancelled' ? 'bg-zinc-800 text-zinc-400' :
                    'bg-red-900/40 text-red-300'
                  }`}>{r.status}</span>
                </td>
                <td className="px-3 py-2">
                  {r.status === 'pending' && (
                    <div className="flex gap-1">
                      <button onClick={() => cancel(r.id)} title="Cancel (client cancelled order)" data-testid={`debt-cancel-${r.id}`}
                        className="p-1.5 rounded bg-zinc-800 text-zinc-400 hover:text-amber-300"><X size={11} /></button>
                      <button onClick={() => del(r.id)} title="Delete" data-testid={`debt-delete-${r.id}`}
                        className="p-1.5 rounded bg-zinc-800 text-zinc-400 hover:text-red-300"><Trash2 size={11} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
